import {OrderDataDTO, QuantityOptionDTO, RequestDisplayDTO, RequestDTO} from "@/api/data-contracts";
import {convertToThreeDigitString, formatCurrency, isDate, parseCurrency, parseNumericString} from "@/lib/utils";
import dayjs from "dayjs";
import {ModelAction} from "@/lib/modelstate";
import {getField, pathOf, setField} from "@/lib/accessor";
import {getCurrentDate} from "@/lib/date-utils";

/**
 * Returns the number of remaining calendar days until the given validUntil date (0–3),
 * or null if the date is missing, invalid, already expired, or more than 3 days away.
 *
 * @param validUntil - ISO date string (YYYY-MM-DD) or null/undefined.
 * @returns 0, 1, 2, or 3 when within the urgency window; null otherwise.
 */
export function remainingValidityDays(validUntil?: string | null): number | null {
    if (!validUntil || !dayjs(validUntil).isValid()) {
        return null;
    }
    const diff = dayjs(validUntil).startOf("day").diff(dayjs(getCurrentDate()).startOf("day"), "day");
    if (diff < 0 || diff > 3) {
        return null;
    }
    return diff;
}

/**
 * Formats a customer quotation number from the given request.
 *
 * @param {RequestDisplayDTO | RequestDTO | undefined | null} request - The request object containing the company number and customer request number.
 * @returns {string | null} - The constructed customer quotation number in the format "XXX-YYY" or null if the request is not provided.
 */
export function formatCustomerRequestNumber(request: RequestDisplayDTO | RequestDTO | undefined | null): string | null {
    if (request?.customerReferenceNumber == null) {
        return null;
    }
    return `${convertToThreeDigitString(request.company?.number)}-${request.customerReferenceNumber}`;
}

export interface TotalPriceDisplayProps {
    locale: string;
    netPriceValue: string | number;
    quantityValue?: string | number | null;
}

export function calcTotalPrice(netPricePerThousand?: number, quantity?: number) {
    return netPricePerThousand == null || quantity == null ? null : (netPricePerThousand * quantity) / 1000;
}

/**
 * Calculates the full price as a currency string based on the provided total price display properties.
 *
 * @param {TotalPriceDisplayProps} totalPriceDisplayProps - The properties used to calculate the full price.
 * @param {string} totalPriceDisplayProps.locale - The locale used for currency formatting.
 * @param {string | number} totalPriceDisplayProps.netPriceValue - The net price value, either as a string or number.
 * @param {string | number | null} [totalPriceDisplayProps.quantityValue] - The quantity value, either as a string, number, or null.
 * @returns {string | null} - The formatted full price as a currency string, or null if the calculation fails.
 */
export function calculateFullPriceAsCurrency(totalPriceDisplayProps: TotalPriceDisplayProps): string | null {
    const parseCurrencyWithLocale = parseCurrency(totalPriceDisplayProps.locale);
    const parseNumericStringWithLocale = parseNumericString(totalPriceDisplayProps.locale);

    const parsedNetPrice =
        typeof totalPriceDisplayProps.netPriceValue === "string"
            ? parseCurrencyWithLocale(totalPriceDisplayProps.netPriceValue)
            : totalPriceDisplayProps.netPriceValue;
    const netPricePerThousand = parsedNetPrice ?? 0;

    const parsedQuantity = parseNumericStringWithLocale(0, totalPriceDisplayProps.quantityValue);
    const quantity = parsedQuantity ?? 0;

    // Nettopreis-frei-Haus-Gesamt = (Nettopreis-frei-Haus-pro-1000 * Anzahl) / 1000
    return formatCurrency(calcTotalPrice(netPricePerThousand, quantity), totalPriceDisplayProps.locale);
}

/**
 * Calculates the new deliveryDateTo value based on deliveryDateFrom and the existing deliveryDateTo.
 * If deliveryDateTo is null, returns deliveryDateFrom (auto-copy).
 * Otherwise, preserves the existing day difference, or resets to deliveryDateFrom if deliveryDateTo is before it.
 *
 * @param deliveryDateFrom - The new deliveryDateFrom value.
 * @param deliveryDateTo - The existing deliveryDateTo value (may be null).
 * @returns The calculated deliveryDateTo value.
 */
export function calcDeliveryDateTo(
    deliveryDateFrom: Date | string,
    deliveryDateTo: Date | string | null | undefined
): Date | string {
    if (deliveryDateTo == null) return deliveryDateFrom;
    const differenceInDays = dayjs(deliveryDateTo).diff(dayjs(deliveryDateFrom), "day");
    return differenceInDays <= 0 ? deliveryDateFrom : dayjs(deliveryDateFrom).add(differenceInDays, "day").toDate();
}

/**
 * Applies quantity option price recalculation if the changed field belongs to a quantity option row.
 *
 * @param model - The current request model.
 * @param field - The field path that changed.
 * @returns The updated model, or null if the field does not belong to a quantity option row.
 */
function applyQuantityOptionEffect(model: RequestDTO, field: string): RequestDTO | null {
    // setField returns any; cast to RequestDTO since the structure is preserved
    const match =
        /orderData\.quantityOptions\[\w+]\./.exec(field)?.[0] && /(orderData\.quantityOptions\[\d+])\./.exec(field);
    if (match) {
        const quantityField = `${match[1]}.quantity`;
        const netPricePer1000Field = `${match[1]}.netPricePer1000`;
        const netPriceTotalField = `${match[1]}.netPriceTotal`;
        return setField(
            model,
            netPriceTotalField,
            calcTotalPrice(getField(model, netPricePer1000Field), getField(model, quantityField))
        ) as RequestDTO;
    }
    return null;
}

/**
 * Handles field-level onChange side effects for the request model.
 * Extracts the nested switch logic from requestReducer to reduce cognitive complexity.
 *
 * @param model - The current request model.
 * @param field - The field path that changed.
 * @returns The updated model, or the original model if no side effect applies.
 */
function applyOnChangeEffect(model: RequestDTO, field: string): RequestDTO {
    // setField returns any; all returns are cast to RequestDTO since the structure is preserved
    switch (field) {
        case pathOf<RequestDTO, OrderDataDTO>("orderData", "allowPalletOverhang"):
            if (!model.orderData?.allowPalletOverhang) {
                return setField(
                    model,
                    pathOf<RequestDTO, OrderDataDTO>("orderData", "palletOverhang"),
                    null
                ) as RequestDTO;
            }
            break;

        case pathOf<RequestDTO, OrderDataDTO>("orderData", "deliveryDateFrom"):
            if (isDate(model.orderData.deliveryDateFrom)) {
                return setField(
                    model,
                    pathOf<RequestDTO, OrderDataDTO>("orderData", "deliveryDateTo"),
                    calcDeliveryDateTo(model.orderData.deliveryDateFrom!, model.orderData.deliveryDateTo)
                ) as RequestDTO;
            }
            break;

        case pathOf<RequestDTO, OrderDataDTO>("orderData", "deliveryDateTo"): {
            const bothDates = isDate(model.orderData.deliveryDateFrom) && isDate(model.orderData.deliveryDateTo);
            const isToBeforeFrom =
                bothDates &&
                dayjs(model.orderData.deliveryDateTo).diff(dayjs(model.orderData.deliveryDateFrom), "day") < 0;
            if (isToBeforeFrom) {
                return setField(
                    model,
                    pathOf<RequestDTO, OrderDataDTO>("orderData", "deliveryDateFrom"),
                    model.orderData.deliveryDateTo
                ) as RequestDTO;
            }
            break;
        }

        case pathOf<RequestDTO, OrderDataDTO>("orderData", "seriesOrder"):
            if (!model.orderData?.seriesOrder) {
                return setField(
                    model,
                    pathOf<RequestDTO, OrderDataDTO>("orderData", "seriesTotalQuantity"),
                    null
                ) as RequestDTO;
            }
            break;
    }

    return applyQuantityOptionEffect(model, field) ?? model;
}

export const requestReducer = (model: RequestDTO, action: ModelAction): RequestDTO => {
    switch (action.type) {
        case "addQuantityRow":
            return {
                ...model,
                orderData: {
                    ...model.orderData,
                    quantityOptions: [
                        ...(model.orderData?.quantityOptions ?? []),
                        {
                            quantity: undefined,
                            netPricePer1000: undefined
                        } as unknown as QuantityOptionDTO
                    ]
                }
            };

        case "remQuantityRow":
            return action.data > 0
                ? {
                    ...model,
                    orderData: {
                        ...model.orderData,
                        quantityOptions: model.orderData?.quantityOptions?.filter((_, index) => index !== action.data)
                    }
                }
                : model;

        case "setDocuments":
            return {
                ...model,
                orderDocuments: {
                    ...model.orderDocuments,
                    documents: action.data
                }
            };

        case "remDocument":
            return {
                ...model,
                orderDocuments: {
                    ...model.orderDocuments,
                    documents: model.orderDocuments?.documents?.filter((o) => o !== action.data)
                }
            };

        case "--onchange":
            return applyOnChangeEffect(model, action.data[0]);
        default:
            return model;
    }
};
